import { Form, Switch, DatePicker, notification } from 'antd'

import { useEffect, useState } from 'react'
import moment from 'moment'

import './TaskDrawer.style.less'
import TextList from './TextList'
import FooterForm from './FooterForm'
import { useUpdateTaskDetail } from '../TaskTable.hook'
import { DATE_FORMAT, convertUtcToLocalDisplay } from '../TaskTable.transform'
import { useAuth } from '@/providers/auth.provider'
import { dateFormat } from '@/utils/date'
import { DATE_FORMAT as DATE_FORMAT_CONST } from '@/constant/date'

interface FooterProps {
  handleCancel?: any
  onFinish?: any
  planDate?: string
  isRangeDate?: boolean
  id: string
  refetch?: any
  onUpdated?: any
  canEdit: boolean
  data: any
}

function UpdatePlanDateForm(props: FooterProps) {
  const {
    planDate,
    isRangeDate = false,
    id,
    refetch,
    onUpdated,
    canEdit,
    data: taskData,
  } = props

  const { user } = useAuth()

  const isUserAssignedAndIsUpdater = (): boolean => {
    const isCurrentUserAssign = taskData.assignee.id === user?.id
    const isUserPartOfCollaboration = taskData?.order?.assignments?.find(
      (assignment) => {
        return assignment?.user?.id === user?.id
      }
    )
    if (
      isCurrentUserAssign &&
      isUserPartOfCollaboration &&
      isUserPartOfCollaboration.roles.role === 'updater'
    ) {
      return false
    }
    return true
  }

  const [form] = Form.useForm()
  const [onEdit, setOnEdit] = useState(false)
  const [hasEndDate, setHasEndDate] = useState(isRangeDate)
  const { mutate, isLoading, isSuccess, data, error, isError } =
    useUpdateTaskDetail()
  const updatedTask = data?.data?.tasks
  const planStartDate = updatedTask?.plan_start_date || taskData.plan_start_date
  const planEndDate = updatedTask?.plan_end_date || taskData.plan_end_date
  const currentIsRangeDate = updatedTask?.is_range_date ?? isRangeDate

  useEffect(() => {
    if (data?.data) {
      setHasEndDate(data?.data?.tasks?.is_range_date)
      onUpdated(data?.data)
    }
  }, [data])

  useEffect(() => {
    if (isError) {
      notification.error({
        message:
          (error as any)?.response?.data?.message ||
          'Unable to update Plan date',
        duration: 4,
        placement: 'top',
      })
    }
  }, [isError])

  useEffect(() => {
    if (!isSuccess) return
    notification.success({
      message: 'Updated Planned Date successfully.',
      duration: 4,
      placement: 'top',
    })
    setOnEdit(false)
    refetch()
  }, [isSuccess])

  const onFinish = (values: any) => {
    // Get the selected dates directly from the form
    const startDate = values.hasEndDate ? values.planDate[0] : values.planDate
    const endDate = values.hasEndDate ? values.planDate[1] : values.planDate

    const planStartDate = startDate.format(DATE_FORMAT_CONST.YYYYMMDD)
    const planEndDate = endDate.format(DATE_FORMAT_CONST.YYYYMMDD)
    const isRangeDate = !!values.hasEndDate
    const payload = {
      id,
      payload: {
        plan_start_date: planStartDate,
        plan_end_date: planEndDate,
        is_range_date: isRangeDate,
      },
      type: 'plandate',
    }

    mutate(payload)
  }

  const handleEdit = () => {
    setHasEndDate(currentIsRangeDate)
    form.setFieldsValue({
      hasEndDate: currentIsRangeDate,
      planDate: currentIsRangeDate
        ? [moment(planStartDate), moment(planEndDate)]
        : planStartDate
        ? moment(planStartDate)
        : null,
    })
    setOnEdit(true)
  }

  const handleCancel = () => {
    setOnEdit(false)
  }
  const handleChangeEndDate = (value) => {
    setHasEndDate(value)
    form.setFieldValue('planDate', null)
  }

  const planDateFormValue = Form.useWatch('planDate', form)

  if (onEdit) {
    return (
      <div className="task-form">
        <Form
          form={form}
          onFinish={onFinish}
          layout="horizontal"
          labelCol={{ span: 10 }}
          wrapperCol={{ span: 14 }}
          colon={false}
        >
          <Form.Item label="Planned Date" name="planDate">
            {!hasEndDate ? (
              <DatePicker
                placeholder="Date"
                size="middle"
                format={DATE_FORMAT}
              />
            ) : (
              <DatePicker.RangePicker size="middle" format={DATE_FORMAT} />
            )}
          </Form.Item>
          <Form.Item label="End Date" name="hasEndDate" valuePropName="checked">
            <Switch onChange={handleChangeEndDate} />
          </Form.Item>
          <FooterForm
            disableSaveButton={!planDateFormValue}
            isLoading={isLoading}
            handleCancel={handleCancel}
          />
        </Form>
      </div>
    )
  }

  const dateText = convertUtcToLocalDisplay(planStartDate) || planDate || '-'
  const startEndDate =
    convertUtcToLocalDisplay(planStartDate) +
    ' - ' +
    convertUtcToLocalDisplay(planEndDate)

  return (
    <TextList
      label="Planned Date"
      value={currentIsRangeDate ? startEndDate : dateText}
      onClickEdit={handleEdit}
      isEditable={isUserAssignedAndIsUpdater() ?? canEdit}
    />
  )
}

export default UpdatePlanDateForm
